/**
 * Komal OS - Design Calculators Store
 * Manages the stateless "calculate" preview, saved calculations, and the
 * engineer review workflow, against the real /api/design API.
 *
 * Note on error extraction: main.py's exception handlers put the error text
 * under response.data.message (for explicit raise HTTPException(...) cases),
 * while FastAPI's automatic 422 validation errors (uncaught by that handler)
 * put it under response.data.detail. Other stores in this app only check
 * .detail, which silently swallows most explicit backend error messages -
 * checking both here so this store doesn't inherit that.
 */

import { create } from 'zustand';
import * as designService from '../services/designService';

const extractErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.detail || fallback;

const useDesignStore = create((set, get) => ({
  // State
  calculatorTypes: [],
  result: null,
  calculations: [],
  selectedCalculation: null,
  isCalculating: false,
  isLoading: false,
  isSubmitting: false,
  error: null,

  // Actions
  clearError: () => set({ error: null }),

  clearResult: () => set({ result: null }),

  fetchCalculatorTypes: async () => {
    try {
      const response = await designService.getCalculatorTypes();
      set({ calculatorTypes: response.data.calculator_types || [] });
      return response.data.calculator_types;
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to load calculator types');
      set({ error: message });
      return [];
    }
  },

  calculate: async (calculatorType, inputs) => {
    set({ isCalculating: true, error: null });
    try {
      const response = await designService.calculate(calculatorType, inputs);
      set({ result: response.data, isCalculating: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = extractErrorMessage(error, 'Calculation failed');
      set({ error: message, isCalculating: false, result: null });
      return { success: false, error: message };
    }
  },

  saveCalculation: async (payload) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await designService.createCalculation(payload);
      set({ isSubmitting: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to save calculation');
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  fetchCalculations: async (filters = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await designService.getCalculations(filters);
      set({ calculations: response.data.calculations || [], isLoading: false });
      return response.data.calculations;
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to load calculations');
      set({ error: message, isLoading: false });
      return [];
    }
  },

  fetchCalculation: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await designService.getCalculation(id);
      set({ selectedCalculation: response.data.calculation, isLoading: false });
      return response.data.calculation;
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to load calculation');
      set({ error: message, isLoading: false });
      return null;
    }
  },

  reviewCalculation: async (id, decision, reviewedById, reviewNotes) => {
    set({ isSubmitting: true, error: null });
    try {
      const response = await designService.reviewCalculation(id, decision, reviewedById, reviewNotes);
      const updated = response.data.calculation;
      set((state) => ({
        calculations: state.calculations.map((c) => (c.id === id ? (updated || { ...c, status: decision }) : c)),
        selectedCalculation:
          state.selectedCalculation?.id === id ? (updated || { ...state.selectedCalculation, status: decision }) : state.selectedCalculation,
        isSubmitting: false,
      }));
      return { success: true, data: response.data };
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to review calculation');
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },

  deleteCalculation: async (id) => {
    set({ isSubmitting: true, error: null });
    try {
      await designService.deleteCalculation(id);
      set((state) => ({
        calculations: state.calculations.filter((c) => c.id !== id),
        isSubmitting: false,
      }));
      return { success: true };
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to delete calculation');
      set({ error: message, isSubmitting: false });
      return { success: false, error: message };
    }
  },
}));

export default useDesignStore;
